"use client";

import { useState } from "react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { formatAgo, type RealityModelState } from "@/lib/reality-model/status";
import { cn } from "@/lib/utils";

import { RealityModelMark } from "./reality-model-mark";

export interface RealityModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: RealityModelState;
  now: number;
  settingsHref?: string;
  onUpdate: (mode: "incremental" | "full") => Promise<unknown>;
}

const HEADLINE: Record<RealityModelState["phase"], string> = {
  fresh: "Up to date",
  updating: "Updating",
  queued: "Starting",
  failed: "Update failed",
  stopped: "Update stopped",
  never: "Not built yet",
};

export function RealityModelDialog({ open, onOpenChange, state, now, settingsHref, onUpdate }: RealityModelDialogProps) {
  const [confirmFull, setConfirmFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setConfirmFull(false);
      setError(null);
    }
    onOpenChange(next);
  };

  const running = state.phase === "updating" || state.phase === "queued";

  const start = async (mode: "incremental" | "full") => {
    setBusy(true);
    setError(null);
    try {
      await onUpdate(mode);
      setConfirmFull(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the update.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm" data-testid="reality-model-dialog" className="max-w-[min(340px,calc(100vw-2*var(--space-md)))]">
        <div className="relative mb-[var(--space-sm)] flex justify-center">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                state.phase === "updating"
                  ? "radial-gradient(60% 80% at 50% 45%, var(--token-accent-magenta-surface), transparent 70%)"
                  : state.phase === "failed"
                    ? "radial-gradient(60% 80% at 50% 45%, var(--token-accent-danger-surface), transparent 70%)"
                    : "radial-gradient(60% 80% at 50% 45%, var(--token-accent-info-surface), transparent 70%)",
            }}
          />
          <RealityModelMark phase={state.phase} step={state.step?.index} size={72} className="relative" />
        </div>

        <DialogTitle className="text-center text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
          Reality Model
        </DialogTitle>
        <DialogDescription className="sr-only">
          Scirix&apos;s living map of your code. Every workspace and agent reads from it.
        </DialogDescription>

        <div className="mt-[var(--space-sm)] text-center">
          <p
            className={cn(
              "text-lg font-semibold text-foreground",
              state.phase === "failed" && "text-[color:var(--token-accent-danger-foreground)]",
            )}
          >
            {HEADLINE[state.phase]}
          </p>
          {subline(state, now) ? (
            <p className="mt-1 text-sm text-muted-foreground" title={state.errorMessage ?? undefined}>
              {subline(state, now)}
            </p>
          ) : null}
        </div>

        {state.phase === "updating" && state.step ? <StepBar state={state} /> : null}

        {state.phase === "fresh" && (state.files !== null || state.symbols !== null) ? (
          <div className="mt-[var(--space-md)] flex justify-center gap-[var(--space-xl)]">
            {state.files !== null ? <Stat value={state.files} label="files" /> : null}
            {state.symbols !== null ? <Stat value={state.symbols} label="symbols" /> : null}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="mt-[var(--space-md)] text-center text-sm text-[color:var(--token-accent-danger-foreground)]">
            {error}
          </p>
        ) : null}

        <div className="mt-[var(--space-lg)] flex flex-col items-center gap-[var(--space-sm)]">
          {confirmFull ? (
            <div className="flex flex-wrap items-center justify-center gap-[var(--space-sm)]">
              <span className="text-sm text-muted-foreground">Re-read all files?</span>
              <Button size="sm" onClick={() => start("full")} disabled={busy}>
                {busy ? "Starting…" : "Rebuild"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmFull(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <Button size="sm" onClick={() => start(state.phase === "never" ? "full" : "incremental")} disabled={busy || running}>
                {running ? "Updating…" : busy ? "Starting…" : state.phase === "never" ? "Build now" : "Update now"}
              </Button>
              <div className="flex items-center gap-[var(--space-md)] text-xs text-muted-foreground">
                {state.phase !== "never" ? (
                  <button
                    type="button"
                    className="underline-offset-4 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50"
                    onClick={() => setConfirmFull(true)}
                    disabled={busy || running}
                  >
                    Full rebuild
                  </button>
                ) : null}
                {settingsHref ? (
                  <Link
                    href={settingsHref}
                    className="underline-offset-4 hover:text-foreground hover:underline"
                    onClick={() => handleOpenChange(false)}
                  >
                    Settings
                  </Link>
                ) : null}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function subline(state: RealityModelState, now: number): string | null {
  switch (state.phase) {
    case "fresh":
      return formatAgo(state.finishedAt, now);
    case "updating":
      return state.step?.label ?? null;
    case "failed":
    case "stopped":
      return "Earlier results still work";
    default:
      return null;
  }
}

function StepBar({ state }: { state: RealityModelState }) {
  const step = state.step;
  if (!step) return null;
  return (
    <div
      className="mx-auto mt-[var(--space-md)] flex w-full max-w-[220px] gap-1"
      role="progressbar"
      aria-label={`Step ${step.index} of ${step.total}`}
      aria-valuemin={0}
      aria-valuemax={step.total}
      aria-valuenow={step.index - 1 + (step.fraction ?? 0)}
    >
      {Array.from({ length: step.total }, (_, i) => {
        const n = i + 1;
        const fill = n < step.index ? 1 : n === step.index ? step.fraction : 0;
        return (
          <div key={n} className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            {n === step.index && fill === null ? (
              <div className="absolute inset-0 animate-pulse rounded-full bg-[color:var(--token-accent-magenta-core)] opacity-60" />
            ) : (
              <div
                className={cn(
                  "h-full rounded-full transition-[width] duration-500",
                  n === step.index
                    ? "bg-[color:var(--token-accent-magenta-core)]"
                    : "bg-[color:var(--token-accent-info-core)]",
                )}
                style={{ width: `${Math.round((fill ?? 0) * 100)}%` }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-center">
      <p className="text-base font-semibold tabular-nums text-foreground">{value.toLocaleString("en-US")}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
